import {useMutation} from '@tanstack/react-query'
import {useFormik} from 'formik'
import {ChangeEvent, FC, useCallback} from 'react'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Link} from 'react-router-dom'
import {FormLabel, FormPassword, FormText, GButton} from 'src/app/components/Libs'
import GIcon from 'src/app/components/Libs/Icon/GIcon'
import {useOnline} from 'src/app/hooks/online.hook'
import {useWebTitle} from 'src/app/hooks/title.hook'
import {loginByEmail} from 'src/app/services'
import AuthRedux from 'src/app/store/Auth/AuthRedux'
import NotifAuthRedux from 'src/app/store/Auth/NotifAuthRedux'
import {RootState} from 'src/app/store/RootReducer'
import {getErrors} from 'src/app/utils/api.utils'
import {toAbsoluteUrl} from 'src/app/utils/asset-helpers.utils'
import AuthScreens from '../../AuthScreens'

const initialValues = {
  organization_id: '',
  email: '',
  password: '',
  captcha: '',
}

const LoginEmail: FC = () => {
  const dispatch = useDispatch()
  useWebTitle('Login')
  const {isOnline, errorOffline} = useOnline()
  const notificationAuth = useSelector<RootState>(
    ({notifAuth}) => notifAuth?.notification,
    shallowEqual
  ) as any

  const {executeRecaptcha} = useGoogleReCaptcha()

  const handleReCaptchaVerify = useCallback(
    async (payload: typeof initialValues) => {
      if (!executeRecaptcha || process.env.REACT_APP_SERVER === 'development') {
        console.log('Execute recaptcha not yet available')
        payload.captcha = process.env.REACT_APP_DEFAULT_CAPTCHA_SITEKEY as string
      } else {
        payload.captcha = await executeRecaptcha()
      }

      return loginByEmail(payload)
    },
    [executeRecaptcha]
  )

  const submitFn = useMutation({
    mutationFn: (payload: typeof initialValues) => handleReCaptchaVerify(payload),
    onSuccess: (result) => {
      const data = result?.data?.response_output?.detail
      const {token, refresh_token} = data

      dispatch(AuthRedux.actions.login(token, refresh_token))
    },
    onError: (e: any) => {
      const status = e.response.status

      if (!isOnline) {
        formik.setStatus(errorOffline)
        return
      }

      if (isOnline) {
        if (status >= 500 || status === 404) {
          formik.setStatus('Layanan Sementara Tidak Tersedia. Silakan coba lagi nanti.')
        } else if (getErrors(e).detailCode === 'INACTIVE_ACCOUNT') {
          formik.setStatus('Akun tidak ditemukan, harap hubungi Administrator')
        } else {
          formik.setStatus('ID Perusahaan/Email/Kata sandi salah')
        }
      }
    },
  })

  const formik = useFormik({
    initialValues,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values) => {
      formik.setStatus('')
      dispatch(NotifAuthRedux.actions.setClearNotification())

      if (isOnline) {
        submitFn.mutate(values)
      } else {
        formik.setStatus(errorOffline)
      }
    },
  })

  const handleChangeCompanyId = (e: ChangeEvent<HTMLInputElement>) => {
    const capitalizedValue = e.target.value.toUpperCase()
    formik.setFieldValue('organization_id', capitalizedValue)
  }

  return (
    <div data-testid='login-email-page' className='w-full'>
      <img
        src={toAbsoluteUrl('/media/logo/logo_optera.svg')}
        alt='optera_logo'
        className='h-[62px] mb-6'
      />

      <div className='mb-6'>
        <div className='mb-2 font-semibold text-fs-5 text-neutral-900'>Selamat Datang</div>
        <div className='text-neutral-600'>Silakan masuk ke akun Anda</div>
      </div>

      <form className='w-full' onSubmit={formik.handleSubmit} noValidate>
        <div className='mb-3'>
          <FormLabel className='mb-2' textClassName='font-normal text-neutral-700' required>
            ID Organisasi
          </FormLabel>
          <FormText
            {...formik.getFieldProps('organization_id')}
            name='organization_id'
            type='text'
            placeholder='Masukkan ID Organisasi'
            maxLength={10}
            error={formik.errors.organization_id}
            touched={formik.touched.organization_id}
            onChange={handleChangeCompanyId}
          />
        </div>

        <div className='mb-3'>
          <FormLabel className='mb-2' textClassName='font-normal text-neutral-700' required>
            Email
          </FormLabel>
          <FormText
            {...formik.getFieldProps('email')}
            name='email'
            type='email'
            placeholder='Masukkan email'
            maxLength={50}
            error={formik.errors.email}
            touched={formik.touched.email}
          />
        </div>

        <div className='mb-6'>
          <FormLabel className='mb-2' textClassName='font-normal text-neutral-700' required>
            Kata Sandi
          </FormLabel>
          <FormPassword
            {...formik.getFieldProps('password')}
            name='password'
            placeholder='Masukkan kata sandi'
            minLength={8}
            error={formik.errors.password}
            touched={formik.touched.password}
          />

          <div className='pb-2 mt-4 font-medium text-end text-primary-500'>
            <Link to={AuthScreens.FORGOT.PATH}>
              <span className='cursor-pointer'>Lupa Kata Sandi</span>
            </Link>
          </div>
        </div>

        {notificationAuth && (
          <div className='flex items-center p-4 mb-6 border rounded-lg drop-shadow-lg border-success-500 bg-success-50'>
            <div className='mr-3 text-success-600'>
              <GIcon icon='IconCheckCircle' className='w-6 h-6' />
            </div>
            <span className='font-semibold text-fs-9 text-neutral-600'>{notificationAuth}</span>
          </div>
        )}

        {formik.status && (
          <div className='flex items-center p-4 mb-6 border rounded-lg drop-shadow-lg border-danger-500 bg-danger-50'>
            <div className='mr-3 text-danger-600'>
              <GIcon icon='IconCheckCircle' className='w-6 h-6' />
            </div>
            <span className='font-semibold text-fs-9 text-neutral-600'>{formik.status}</span>
          </div>
        )}

        <GButton
          type='submit'
          size='large'
          className='w-full'
          loading={submitFn.isLoading}
          disabled={submitFn.isLoading}
        >
          Masuk
        </GButton>
      </form>
    </div>
  )
}

export default LoginEmail
